import { useState, useRef, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, Button } from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import FlexBox from "components/common/FlexBox";
import {
  BookOpenIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronLeft,
  ChevronRight,
  HandshakeIcon,
  LockIcon,
  SmartphoneCharging,
  Telescope,
  X,
} from "lucide-react";
import { ReactComponent as HeartTopIcon } from "../../icons/Menu/HeartTop.svg";
import { CurrentUserContext } from "components/context/RequireAuth";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import useDigitalSubscriptionUX from "components/hooks/useDigitalSubscriptionUX";
import { resolveFeatureSuggestionDescription } from "common/memberModuleResolver";

interface FeatureTile {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  link?: string;
  onClick?: () => void;
  openInNewTab?: boolean;
}

const DEFAULT_FEATURES: FeatureTile[] = [
  {
    id: "delegation-guide",
    title: "Delegation Guide",
    description: "Learn how to effectively delegate tasks to your advisor.",
    icon: BookOpenIcon,
    link: "https://app.findfaye.com/documents/ba21fb0d-f924-436a-bafb-4411a82c6b0b",
  },
  {
    id: "calendar-setup",
    title: "Calendar Setup",
    description: "Connect your calendars for seamless scheduling.",
    icon: ArrowPathIcon,
    link: "https://app.findfaye.com/documents/0fe2b424-bf75-4551-8e84-8dd850db7508",
  },
  {
    id: "household-info",
    title: "Household Info",
    description:
      "Share household details with your Advisor for less back and forth.",
    icon: HeartTopIcon,
    link: "https://app.findfaye.com/forms/complete/c2947631-591e-458d-bc64-5952356e82a7",
  },
  {
    id: "magic-calendar",
    title: "Magic Calendar",
    description: "Let Faye automatically manage your family calendar.",
    icon: CalendarDaysIcon,
    link: "https://app.findfaye.com/documents/816364b7-0340-4eeb-84c6-6b97cda7c160",
  },
  {
    id: "work-with-me-guide",
    title: "How To Work With Me",
    description: "Quick form to tell your advisor how to best work with you.",
    icon: HandshakeIcon,
    link: "https://app.findfaye.com/forms/complete/b1084dae-0f3e-4868-b148-cc9a47d33b82",
  },
  {
    id: "starter-tasks",
    title: "Starter Tasks",
    description: "Get started with popular tasks other families love.",
    icon: ClipboardDocumentListIcon,
    link: "/task-discovery",
  },
  {
    id: "family-secrets",
    title: "Family Secrets",
    description:
      "How to use family secrets to share sensitive information with your advisor.",
    icon: LockIcon,
    link: "https://app.findfaye.com/documents/80671410-6784-41b9-8b20-c020e095222f",
  },
  {
    id: "download-app",
    title: "Download Web App",
    description: "Download the Faye web app a better mobile experience.",
    icon: SmartphoneCharging,
    link: "https://app.findfaye.com/documents/81849feb-b842-460d-9090-e97e569e4a8d",
  },
];

interface FeatureSuggestionsModuleProps {
  features?: FeatureTile[];
  onFeatureClick?: (featureId: string) => void;
  onDismiss?: () => void;
  title?: string;
  subtitle?: string;
}

const chevronButtonSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  backgroundColor: "white",
  border: "1px solid #D4D4D4",
  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.10)",
};

export default ({
  features: featuresProp,
  onFeatureClick,
  onDismiss,
  title,
  subtitle,
}: FeatureSuggestionsModuleProps) => {
  const navigate = useNavigate();
  const currentUser = useContext(CurrentUserContext);
  const isDigitalSubscription = useDigitalSubscriptionUX();
  const planFlags = {
    ...planFlagsFromStatus(currentUser.familyStatus),
    isDigitalSubscription,
  };
  const displayFeatures = useMemo(() => {
    const base = featuresProp ?? DEFAULT_FEATURES;
    if (featuresProp !== undefined) {
      return base;
    }
    if (!isDigitalSubscription) {
      return base;
    }
    return base.map((tile) => ({
      ...tile,
      description: resolveFeatureSuggestionDescription(
        tile.id,
        tile.description,
        planFlags,
      ),
    }));
  }, [featuresProp, currentUser.familyStatus, isDigitalSubscription]);
  const isMobile = useIsMobile();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showNextChevron, setShowNextChevron] = useState(false);
  const [showBackChevron, setShowBackChevron] = useState(false);
  const tileWidth = isMobile ? 260 : 220;
  const gapWidth = 16;

  const checkScrollable = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const hasMoreContent =
        container.scrollWidth >
        container.clientWidth + container.scrollLeft + 10;
      setShowNextChevron(hasMoreContent);
      setShowBackChevron(container.scrollLeft > 10);
    }
  };

  const scrollToCardIndex = (targetIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const padding = 20; // left padding from sx prop
    const cardSize = tileWidth + gapWidth;
    const cardLeft = padding + targetIndex * cardSize; // Account for left padding
    const cardCenter = cardLeft + tileWidth / 2;
    const scrollPosition = cardCenter - container.clientWidth / 2;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const finalPosition = Math.min(Math.max(0, scrollPosition), maxScroll);

    container.scrollTo({
      left: finalPosition,
      behavior: "smooth",
    });
  };

  const scrollToNext = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (isMobile) {
      const currentIndex = Math.round(
        container.scrollLeft / (tileWidth + gapWidth),
      );
      scrollToCardIndex(currentIndex + 1);
    } else {
      container.scrollBy({
        left: tileWidth + gapWidth,
        behavior: "smooth",
      });
    }
  };

  const scrollToPrev = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (isMobile) {
      const currentIndex = Math.round(
        container.scrollLeft / (tileWidth + gapWidth),
      );
      scrollToCardIndex(Math.max(0, currentIndex - 1));
    } else {
      container.scrollBy({
        left: -(tileWidth + gapWidth),
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    checkScrollable();
    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, [displayFeatures.length]);

  return (
    <FlexBox
      variant={isMobile ? "default" : "contained"}
      direction="column"
      gap="8px"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <Telescope size={20} color="#198282" />
          <Typography variant={isMobile ? "h3" : "h4"}>
            {title ||
              (!isMobile
                ? "Explore what you can do with Faye"
                : "Explore Faye's features")}
          </Typography>
        </Box>
        {onDismiss && (
          <>
            <IconButton
              onClick={onDismiss}
              size="small"
              sx={{
                color: "#6B7280",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              <X style={{ width: "20px", height: "20px", color: "#8E9598" }} />
            </IconButton>
          </>
        )}
      </Box>
      {subtitle && <Typography variant="bodySmall">{subtitle}</Typography>}
      <Box
        sx={{
          position: "relative",
          width: "calc(100% + 40px) !important",
          marginLeft: "-20px",
        }}
      >
        <Box
          ref={scrollContainerRef}
          onScroll={checkScrollable}
          sx={{
            display: "flex",
            gap: "16px",
            padding: "8px 20px",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {displayFeatures.map((feature) => {
            const IconComponent = feature.icon;
            return (
              <Box
                key={feature.id}
                onClick={() => {
                  if (feature.link) {
                    if (feature.openInNewTab ?? true) {
                      window.open(feature.link);
                    } else {
                      navigate(feature.link);
                    }
                  }
                  feature.onClick?.();
                  onFeatureClick?.(feature.id);
                }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  padding: "16px",
                  minWidth: isMobile ? "260px" : "220px",
                  maxWidth: isMobile ? "260px" : "220px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                  cursor: "pointer",
                  scrollSnapAlign: "start",
                  justifyContent: "space-between",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    borderColor: "#DEEDED",
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.10)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#F0F9F9",
                    }}
                  >
                    <IconComponent
                      style={{
                        width: "20px",
                        height: "20px",
                        color: "#198282",
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <Typography variant={isMobile ? "h5" : "bodyHeavy"}>
                      {feature.title}
                    </Typography>
                    <Typography variant="bodySmall">
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="text"
                  sx={{
                    alignSelf: "start",
                    height: "unset",
                  }}
                  endIcon={<ChevronRight size={20} color="#198282" />}
                >
                  Learn more
                </Button>
              </Box>
            );
          })}
        </Box>

        {showBackChevron && (
          <IconButton
            onClick={scrollToPrev}
            disableRipple
            sx={{
              position: "absolute",
              padding: "0 20px 0 12px",
              height: "100%",
              left: isMobile ? "-2px" : 0,
              top: "50%",
              transform: "translateY(-50%)",
              background:
                "linear-gradient(to left, transparent, rgba(255, 255, 255, 0.8) 100%)",
              borderRadius: 0,
            }}
          >
            <Box sx={chevronButtonSx}>
              <ChevronLeft size={24} color="#6B7280" />
            </Box>
          </IconButton>
        )}

        {showNextChevron && (
          <IconButton
            onClick={scrollToNext}
            disableRipple
            sx={{
              position: "absolute",
              height: "100%",
              right: isMobile ? "-2px" : 0,
              padding: "0 12px 0 20px",
              top: "50%",
              transform: "translateY(-50%)",
              background:
                "linear-gradient(to right, transparent, rgba(255, 255, 255, 0.8) 100%)",
              borderRadius: 0,
            }}
          >
            <Box sx={chevronButtonSx}>
              <ChevronRight size={24} color="#6B7280" />
            </Box>
          </IconButton>
        )}
      </Box>
    </FlexBox>
  );
};
